import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import FooterNav from '../components/ui/footer-nav';
import NavMenu from '../components/ui/nav-menu';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { 
  UserPlus, FileEdit, Calculator, FileSpreadsheet, ChevronRight,
  Loader2, FileX, MapPin, Clock, AlertCircle, Users, Check,
  ClipboardCheck, ShieldCheck, GitBranch, UserCheck,
  FileText, Banknote, Bell, CheckCircle, Plus, Building2,
  Presentation, Award, CircleCheck, Sparkles, Mail,
  Trophy, Truck, Search, ListFilter, Inbox, Hourglass, CheckCircle2, Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnifiedWorkspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const carouselRef = useRef(null);
  
  // Determine initial workspace based on route
  const getInitialWorkspace = () => {
    if (location.pathname.includes('win')) return 0;
    if (location.pathname.includes('deliver')) return 1;
    if (location.pathname.includes('get-paid')) return 2;
    return 1; // Default to Deliver
  };
  
  const [activeWorkspace, setActiveWorkspace] = useState(getInitialWorkspace());
  const [leads, setLeads] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingDone, setMarkingDone] = useState(null);
  const [markingLeadDone, setMarkingLeadDone] = useState(null);
  const [deletingUpdate, setDeletingUpdate] = useState(null);
  const [deletingLead, setDeletingLead] = useState(null);
  
  // Active module within each workspace
  const [winModule, setWinModule] = useState('lead');
  const [deliverModule, setDeliverModule] = useState('update');
  const [getPaidModule, setGetPaidModule] = useState('claim');
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState('');
  
  // Search queries for each workspace
  const [winSearch, setWinSearch] = useState('');
  const [deliverSearch, setDeliverSearch] = useState('');
  const [paidSearch, setPaidSearch] = useState('');
  
  // Status filters for each workspace
  const [winStatusFilter, setWinStatusFilter] = useState('all');
  const [deliverStatusFilter, setDeliverStatusFilter] = useState('all');
  const [paidStatusFilter, setPaidStatusFilter] = useState('all');

  const workspaces = [
    {
      id: 'win',
      title: 'Win Work',
      stage: 'WIN WORK',
      stageColor: 'text-emerald-400',
      barColor: 'bg-emerald-500',
      accentColor: 'emerald',
      buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
      path: '/tradie/win',
      icon: Trophy
    },
    {
      id: 'deliver',
      title: 'Deliver Work',
      stage: 'DELIVER WORK',
      stageColor: 'text-blue-400',
      barColor: 'bg-blue-500',
      accentColor: 'blue',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
      path: '/tradie/deliver',
      icon: Truck
    },
    {
      id: 'get-paid',
      title: 'Get Paid',
      stage: 'GET PAID',
      stageColor: 'text-orange-400',
      barColor: 'bg-orange-500',
      accentColor: 'orange',
      buttonColor: 'bg-orange-600 hover:bg-orange-700',
      path: '/tradie/get-paid',
      icon: Banknote
    }
  ];

  const winModules = [
    { id: 'lead', label: 'Lead / Referral', icon: UserPlus, path: '/tradie/leads-referral', buttonLabel: 'Add New Lead / Referral', buttonIcon: UserPlus },
    { id: 'scope', label: 'Generate Scope', icon: FileEdit, path: '/tradie/capture-job-notes', buttonLabel: 'Generate New Scope', buttonIcon: FileEdit },
    { id: 'costing', label: 'Costing', icon: Calculator, path: null, buttonLabel: 'Create Costing', buttonIcon: Calculator },
    { id: 'quote', label: 'Quote', icon: FileSpreadsheet, path: null, buttonLabel: 'Create Quote', buttonIcon: FileSpreadsheet },
    { id: 'proposal', label: 'Proposal', icon: Presentation, path: null, buttonLabel: 'Create Proposal', buttonIcon: Presentation },
    { id: 'create-win', label: 'Create Your Own', icon: Plus, path: null, buttonLabel: 'Create Your Own', buttonIcon: Plus },
  ];

  const deliverModules = [
    { id: 'update', label: 'Site Update', icon: ClipboardCheck, path: '/tradie/log-update', buttonLabel: 'Log Site Update', buttonIcon: ClipboardCheck },
    { id: 'swms', label: 'SWMS', icon: ShieldCheck, path: '/tradie/generate-swms', buttonLabel: 'Generate SWMS', buttonIcon: ShieldCheck },
    { id: 'signoff', label: 'Record Sign-Off', icon: UserCheck, path: null, buttonLabel: 'Record Sign-Off', buttonIcon: UserCheck },
    { id: 'variation', label: 'Variation', icon: GitBranch, path: null, buttonLabel: 'Log Variation', buttonIcon: GitBranch },
    { id: 'certificate', label: 'Certificate of Compliance', icon: Award, path: null, buttonLabel: 'Create Certificate', buttonIcon: Award },
    { id: 'completion', label: 'Job Completion', icon: CircleCheck, path: null, buttonLabel: 'Mark Job Complete', buttonIcon: CircleCheck },
    { id: 'create-deliver', label: 'Create Your Own', icon: Plus, path: null, buttonLabel: 'Create Your Own', buttonIcon: Plus },
  ];

  const getPaidModules = [
    { id: 'claim', label: 'Claim / Invoice', icon: FileText, path: '/tradie/claim', buttonLabel: 'Create Claim / Invoice', buttonIcon: FileText },
    { id: 'payment', label: 'Log Payment', icon: Banknote, path: null, buttonLabel: 'Log Payment', buttonIcon: Banknote },
    { id: 'reminder', label: 'Send Reminder', icon: Bell, path: null, buttonLabel: 'Send Reminder', buttonIcon: Bell },
    { id: 'create-paid', label: 'Create Your Own', icon: Plus, path: null, buttonLabel: 'Create Your Own', buttonIcon: Plus },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Update workspace when route changes
  useEffect(() => {
    const newWorkspace = getInitialWorkspace();
    if (newWorkspace !== activeWorkspace) {
      setActiveWorkspace(newWorkspace);
      // Scroll to the correct workspace
      if (carouselRef.current) {
        const containerWidth = carouselRef.current.offsetWidth;
        carouselRef.current.scrollTo({
          left: newWorkspace * containerWidth,
          behavior: 'smooth'
        });
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    // Scroll to correct workspace on load
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollLeft = activeWorkspace * containerWidth;
    }
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, updatesRes] = await Promise.all([
        axios.get(`${API_URL}/leads/my`),
        axios.get(`${API_URL}/updates/my`)
      ]);
      setLeads(leadsRes.data);
      setUpdates(updatesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollPos = carouselRef.current.scrollLeft;
      const containerWidth = carouselRef.current.offsetWidth;
      const newActive = Math.round(scrollPos / containerWidth);
      if (newActive !== activeWorkspace) {
        setActiveWorkspace(Math.max(0, Math.min(2, newActive)));
      }
    }
  };

  const scrollToWorkspace = (index) => {
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollTo({
        left: index * containerWidth,
        behavior: 'smooth'
      });
    }
  };

  const handleMarkDone = async (e, updateId) => {
    e.stopPropagation();
    setMarkingDone(updateId);
    try {
      await axios.put(`${API_URL}/updates/${updateId}`, { status: 'Done' });
      setUpdates(prev => prev.map(u => u.id === updateId ? { ...u, status: 'Done' } : u));
      toast.success('Marked as Done!');
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setMarkingDone(null);
    }
  };

  const handleMarkLeadDone = async (e, leadId) => {
    e.stopPropagation();
    setMarkingLeadDone(leadId);
    try {
      await axios.patch(`${API_URL}/leads/${leadId}`, { status: 'Done' });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: 'Done' } : l));
      toast.success('Marked as Done!');
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setMarkingLeadDone(null);
    }
  };

  const handleDeleteUpdate = async (e, updateId) => {
    e.stopPropagation();
    setDeletingUpdate(updateId);
    try {
      await axios.delete(`${API_URL}/updates/${updateId}`);
      setUpdates(prev => prev.filter(u => u.id !== updateId));
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeletingUpdate(null);
    }
  };

  const handleDeleteLead = async (e, leadId) => {
    e.stopPropagation();
    setDeletingLead(leadId);
    try {
      await axios.delete(`${API_URL}/leads/${leadId}`);
      setLeads(prev => prev.filter(l => l.id !== leadId));
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeletingLead(null);
    }
  };

  const counts = useMemo(() => ({
    leads: leads.length,
    updates: updates.length,
    claims: 0
  }), [leads, updates]);

  // Filtered data based on search and status
  const filteredLeads = useMemo(() => {
    let result = leads;
    
    // Apply status filter - exclude Done/Won from All Open, New, In Progress
    result = result.filter(lead => {
      const status = (lead.status || 'new').toLowerCase();
      const isDone = status === 'done' || status === 'won' || status === 'converted' || status === 'lost';
      
      if (winStatusFilter === 'all') return !isDone; // All Open = exclude done
      if (winStatusFilter === 'new') return status === 'new';
      if (winStatusFilter === 'in-progress') return status === 'in progress' || status === 'in-progress';
      if (winStatusFilter === 'done') return isDone;
      return !isDone;
    });
    
    // Apply search filter
    if (winSearch.trim()) {
      const query = winSearch.toLowerCase();
      result = result.filter(lead => 
        (lead.summary_text && lead.summary_text.toLowerCase().includes(query)) ||
        (lead.main_contact && lead.main_contact.toLowerCase().includes(query)) ||
        (lead.site_location && lead.site_location.toLowerCase().includes(query)) ||
        (lead.entry_type_display && lead.entry_type_display.toLowerCase().includes(query)) ||
        (lead.reference_id && lead.reference_id.toLowerCase().includes(query))
      );
    }
    
    return result;
  }, [leads, winSearch, winStatusFilter]);

  const filteredUpdates = useMemo(() => {
    let result = updates;
    
    // Apply status filter - exclude Done from All Open, New, In Progress
    result = result.filter(update => {
      const status = (update.status || 'new').toLowerCase();
      const isDone = status === 'done';
      
      if (deliverStatusFilter === 'all') return !isDone; // All Open = exclude done
      if (deliverStatusFilter === 'new') return status === 'new';
      if (deliverStatusFilter === 'in-progress') return status === 'in progress' || status === 'in-progress';
      if (deliverStatusFilter === 'done') return isDone;
      return !isDone;
    });
    
    // Apply search filter
    if (deliverSearch.trim()) {
      const query = deliverSearch.toLowerCase();
      result = result.filter(update => 
        (update.update_text && update.update_text.toLowerCase().includes(query)) ||
        (update.job_name && update.job_name.toLowerCase().includes(query)) ||
        (update.site_address && update.site_address.toLowerCase().includes(query)) ||
        (update.category && update.category.toLowerCase().includes(query))
      );
    }
    
    return result;
  }, [updates, deliverSearch, deliverStatusFilter]);

  const activeWS = workspaces[activeWorkspace];

  const getStatusClass = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-700';
      case 'In Progress': return 'bg-amber-100 text-amber-700';
      case 'Done': return 'bg-green-100 text-green-700';
      case 'Won': return 'bg-green-100 text-green-700';
      case 'Lost': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const renderModuleRail = (modules, activeModule, setActiveModule, accentColor) => (
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-lg mx-auto overflow-x-auto scrollbar-hide">
        <div className="flex">
          {modules.map((module) => {
            const Icon = module.icon;
            const isActive = activeModule === module.id;
            const isPlaceholder = !module.path;
            return (
              <button
                key={module.id}
                onClick={() => {
                  if (module.path) {
                    setActiveModule(module.id);
                  } else {
                    setSelectedFeature(module.label);
                    setShowContactModal(true);
                  }
                }}
                className={`
                  flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap transition-colors
                  ${isActive ? `border-${accentColor}-500 text-${accentColor}-600` : 'border-transparent text-slate-500'}
                  ${isPlaceholder ? 'opacity-60 hover:opacity-80' : `hover:text-${accentColor}-600`}
                `}
                style={isActive ? { borderColor: accentColor === 'emerald' ? '#10b981' : accentColor === 'blue' ? '#3b82f6' : '#f97316' } : {}}
              >
                <Icon className="w-4 h-4" style={isActive ? { color: accentColor === 'emerald' ? '#10b981' : accentColor === 'blue' ? '#3b82f6' : '#f97316' } : {}} />
                <span className="text-sm font-medium">{module.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Dynamic Header */}
      <header className="bg-[#1F2A37] text-white px-4 py-3 sticky top-0 z-20 relative">
        <div className={`h-1 ${activeWS.barColor} absolute top-0 left-0 right-0 transition-colors duration-300`}></div>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {activeWS.icon && <activeWS.icon className={`w-7 h-7 ${activeWS.stageColor}`} />}
            <div>
              <h1 className="text-xl font-bold">{user?.name?.split(' ')[0] || 'Your'}'s Tasks</h1>
              <p className={`${activeWS.stageColor} text-xs font-semibold uppercase tracking-wide transition-colors duration-300`}>{activeWS.stage}</p>
            </div>
          </div>
          <NavMenu />
        </div>
      </header>

      {/* Swipeable Workspaces */}
      <div 
        ref={carouselRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {/* WIN WORK */}
        <div className="flex-shrink-0 w-full snap-center" style={{ minWidth: '100%' }}>
          {renderModuleRail(winModules, winModule, setWinModule, 'emerald')}
          <main className="p-4 max-w-lg mx-auto">
            {(() => {
              const currentMod = winModules.find(m => m.id === winModule) || winModules[0];
              const ButtonIcon = currentMod.buttonIcon;
              return (
                <button
                  onClick={() => navigate(currentMod.path || '/tradie/leads-referral')}
                  className="w-full mb-4 flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-colors"
                >
                  <ButtonIcon className="w-5 h-5" />
                  {currentMod.buttonLabel}
                </button>
              );
            })()}

            {/* Search & Filters Container */}
            <Card className="border-2 border-slate-200 shadow-sm mb-4">
              <CardContent className="p-4">
                {/* Search Bar */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search leads, referrals..."
                    value={winSearch}
                    onChange={(e) => setWinSearch(e.target.value)}
                    className="pl-10 h-11 border-2 border-slate-200 rounded-xl bg-slate-50"
                  />
                </div>

                {/* Status Filter */}
                <div className="flex bg-emerald-50 rounded-lg p-1">
                  {[
                    { id: 'all', label: 'All Open', icon: ListFilter },
                    { id: 'new', label: 'New', icon: Inbox },
                    { id: 'in-progress', label: 'In Progress', icon: Hourglass },
                    { id: 'done', label: 'Done', icon: CheckCircle2 }
                  ].map((status) => {
                    const Icon = status.icon;
                    const isActive = winStatusFilter === status.id;
                    return (
                      <button
                        key={status.id}
                        onClick={() => setWinStatusFilter(status.id)}
                        className={`flex-1 flex flex-col items-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                          isActive
                            ? 'bg-white shadow-sm text-emerald-600 border-b-2 border-emerald-500'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {status.label}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-12">
                <FileX className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-600">{winSearch ? 'No matches found' : 'No leads yet'}</h3>
                <p className="text-sm text-slate-500 mt-1">{winSearch ? 'Try a different search' : 'Capture your first lead to get started'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredLeads.slice(0, 10).map((lead) => (
                  <Card
                    key={lead.id}
                    onClick={() => navigate(`/tradie/lead/${lead.id}`)}
                    className="p-4 border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-600" />
                        <span className="font-semibold text-slate-800">{lead.entry_type_display || 'Lead'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${getStatusClass(lead.status || 'New')}`}>
                          {lead.status || 'New'}
                        </span>
                        <span className="text-xs font-mono text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                          #{lead.reference_id}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">{lead.summary_text}</p>
                    {lead.site_location && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <MapPin className="w-3 h-3" />
                        {lead.site_location}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <span className="text-xs text-slate-400">{format(new Date(lead.created_at), 'MMM d, h:mm a')}</span>
                      <div className="flex items-center gap-2">
                        {(lead.status !== 'Done' && lead.status !== 'Won') && (
                          <button
                            onClick={(e) => handleMarkLeadDone(e, lead.id)}
                            disabled={markingLeadDone === lead.id}
                            className="flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs font-medium"
                          >
                            {markingLeadDone === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            Done
                          </button>
                        )}
                        {winStatusFilter === 'done' && (
                          <button
                            onClick={(e) => handleDeleteLead(e, lead.id)}
                            disabled={deletingLead === lead.id}
                            className="flex items-center gap-1 px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-medium"
                          >
                            {deletingLead === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            Delete
                          </button>
                        )}
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </main>
        </div>

        {/* DELIVER WORK */}
        <div className="flex-shrink-0 w-full snap-center" style={{ minWidth: '100%' }}>
          {renderModuleRail(deliverModules, deliverModule, setDeliverModule, 'blue')}
          <main className="p-4 max-w-lg mx-auto">
            {(() => {
              const currentMod = deliverModules.find(m => m.id === deliverModule) || deliverModules[0];
              const ButtonIcon = currentMod.buttonIcon;
              return (
                <button
                  onClick={() => navigate(currentMod.path || '/tradie/log-update')}
                  className="w-full mb-4 flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors"
                >
                  <ButtonIcon className="w-5 h-5" />
                  {currentMod.buttonLabel}
                </button>
              );
            })()}

            {/* Search & Filters Container */}
            <Card className="border-2 border-slate-200 shadow-sm mb-4">
              <CardContent className="p-4">
                {/* Search Bar */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search jobs, updates..."
                    value={deliverSearch}
                    onChange={(e) => setDeliverSearch(e.target.value)}
                    className="pl-10 h-11 border-2 border-slate-200 rounded-xl bg-slate-50"
                  />
                </div>

                {/* Status Filter */}
                <div className="flex bg-blue-50 rounded-lg p-1">
                  {[
                    { id: 'all', label: 'All Open', icon: ListFilter },
                    { id: 'new', label: 'New', icon: Inbox },
                    { id: 'in-progress', label: 'In Progress', icon: Hourglass },
                    { id: 'done', label: 'Done', icon: CheckCircle2 }
                  ].map((status) => {
                    const Icon = status.icon;
                    const isActive = deliverStatusFilter === status.id;
                    return (
                      <button
                        key={status.id}
                        onClick={() => setDeliverStatusFilter(status.id)}
                        className={`flex-1 flex flex-col items-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                          isActive
                            ? 'bg-white shadow-sm text-blue-600 border-b-2 border-blue-500'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {status.label}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : filteredUpdates.length === 0 ? (
              <div className="text-center py-12">
                <FileX className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-600">{deliverSearch ? 'No matches found' : 'No updates yet'}</h3>
                <p className="text-sm text-slate-500 mt-1">{deliverSearch ? 'Try a different search' : 'Log your first site update'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredUpdates.slice(0, 10).map((update) => (
                  <Card
                    key={update.id}
                    onClick={() => navigate(`/tradie/update/${update.id}`)}
                    className="p-4 border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-semibold text-slate-800">{update.job_name || 'Untitled'}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${getStatusClass(update.status)}`}>
                        {update.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">{update.update_text}</p>
                    {update.site_address && (
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <MapPin className="w-3 h-3" />
                        {update.site_address}
                      </div>
                    )}
                    {update.required_by && (
                      <div className="flex items-center gap-1 text-xs text-orange-600">
                        <Clock className="w-3 h-3" />
                        Due: {format(new Date(update.required_by), 'MMM d')}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <span className="text-xs text-slate-400">{format(new Date(update.created_at), 'MMM d, h:mm a')}</span>
                      <div className="flex items-center gap-2">
                        {update.urgency && update.urgency !== 'Low' && (
                          <span className={`flex items-center gap-1 text-xs ${update.urgency === 'High' ? 'text-red-500' : 'text-amber-500'}`}>
                            <AlertCircle className="w-3 h-3" />
                            {update.urgency}
                          </span>
                        )}
                        {update.status !== 'Done' && (
                          <button
                            onClick={(e) => handleMarkDone(e, update.id)}
                            disabled={markingDone === update.id}
                            className="flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs font-medium"
                          >
                            {markingDone === update.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            Done
                          </button>
                        )}
                        {deliverStatusFilter === 'done' && (
                          <button
                            onClick={(e) => handleDeleteUpdate(e, update.id)}
                            disabled={deletingUpdate === update.id}
                            className="flex items-center gap-1 px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-medium"
                          >
                            {deletingUpdate === update.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            Delete
                          </button>
                        )}
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </main>
        </div>

        {/* GET PAID */}
        <div className="flex-shrink-0 w-full snap-center" style={{ minWidth: '100%' }}>
          {renderModuleRail(getPaidModules, getPaidModule, setGetPaidModule, 'orange')}
          <main className="p-4 max-w-lg mx-auto">
            {(() => {
              const currentMod = getPaidModules.find(m => m.id === getPaidModule) || getPaidModules[0];
              const ButtonIcon = currentMod.buttonIcon;
              return (
                <button
                  onClick={() => navigate(currentMod.path || '/tradie/claim')}
                  className="w-full mb-4 flex items-center justify-center gap-2 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-semibold transition-colors"
                >
                  <ButtonIcon className="w-5 h-5" />
                  {currentMod.buttonLabel}
                </button>
              );
            })()}

            {/* Search & Filters Container */}
            <Card className="border-2 border-slate-200 shadow-sm mb-4">
              <CardContent className="p-4">
                {/* Search Bar */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search claims, invoices..."
                    value={paidSearch}
                    onChange={(e) => setPaidSearch(e.target.value)}
                    className="pl-10 h-11 border-2 border-slate-200 rounded-xl bg-slate-50"
                  />
                </div>

                {/* Status Filter */}
                <div className="flex bg-orange-50 rounded-lg p-1">
                  {[
                    { id: 'all', label: 'All Open', icon: ListFilter },
                    { id: 'new', label: 'New', icon: Inbox },
                    { id: 'in-progress', label: 'Pending', icon: Hourglass },
                    { id: 'done', label: 'Paid', icon: CheckCircle2 }
                  ].map((status) => {
                    const Icon = status.icon;
                    const isActive = paidStatusFilter === status.id;
                    return (
                      <button
                        key={status.id}
                        onClick={() => setPaidStatusFilter(status.id)}
                        className={`flex-1 flex flex-col items-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                          isActive
                            ? 'bg-white shadow-sm text-orange-600 border-b-2 border-orange-500'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {status.label}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="font-semibold text-slate-700 text-lg">Claims & Invoices</h3>
              <p className="text-sm text-slate-500 mt-2 max-w-xs mx-auto">
                Track what needs to be claimed or invoiced. Capture now, structure later.
              </p>
            </div>
          </main>
        </div>
      </div>

      {/* Dot Indicators */}
      <div className="flex items-center justify-center gap-2 py-2 bg-slate-50">
        {workspaces.map((ws, index) => (
          <button
            key={ws.id}
            onClick={() => scrollToWorkspace(index)}
            className={`h-2 rounded-full transition-all ${
              activeWorkspace === index 
                ? `w-6 ${ws.barColor}` 
                : 'w-2 bg-slate-300'
            }`}
          />
        ))}
      </div>

      <FooterNav counts={counts} />

      {/* Feature Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-sm mx-4 rounded-2xl border-0 p-0 overflow-hidden">
          {/* Compact header with gradient */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 px-6 py-5">
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {selectedFeature}
              </h2>
            </div>
          </div>
          
          {/* Body */}
          <div className="px-6 py-5">
            <p className="text-slate-600 text-center text-sm mb-5">
              This feature can be added to your 2Tap setup. Get in touch with the team to discuss your requirements.
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl"
                onClick={() => {
                  setShowContactModal(false);
                  const subject = encodeURIComponent(`2Tap Feature Request: ${selectedFeature}`);
                  const body = encodeURIComponent(`Hi 2Tap Team,

I'm interested in the **${selectedFeature}** feature in 2Tap.

Here are my details:
• Name: ${user?.name || 'N/A'}
• Email: ${user?.email || 'N/A'}

Appreciate it — keen to hear what's possible.

Thanks 2Tap!`);
                  window.location.href = `mailto:tradeofficeai@gmail.com?subject=${subject}&body=${body}`;
                }}
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact 2Tap Team
              </Button>
              <Button
                variant="ghost"
                className="w-full h-10 text-slate-500 hover:text-slate-700 font-medium"
                onClick={() => setShowContactModal(false)}
              >
                Maybe later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedWorkspace;
